<template>
  <div
    ref="triggerRef"
    class="popover-trigger"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <slot
      name="reference"
      :visible="visible"
    />

    <Teleport to="body">
      <Transition name="popover-fade">
        <div
          v-if="visible"
          ref="popoverRef"
          class="popover"
          :class="[`popover--${placement}`, popoverClass]"
          :style="{
            ...popoverStyle,
          }"
          @mouseenter="handlePopoverMouseEnter"
          @mouseleave="handlePopoverMouseLeave"
        >
          <div
            :style="arrowStyle"
            class="popover__arrow"
          />
          <div class="popover__content">
            <slot :hide="hidePopover" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  placement: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
  },
  trigger: {
    type: String,
    default: 'hover',
    validator: (value) => ['hover', 'click'].includes(value)
  },
  popoverClass: {
    type: String,
    default: ''
  },
  offset: {
    type: Number,
    default: 8
  },
  disabled: {
    type: Boolean,
    default: false
  },
  arrowColor: {
    type: String,
    default: '#ffffff'
  }
})

const emit = defineEmits(['show', 'hide'])

const triggerRef = ref(null)
const popoverRef = ref(null)
const visible = ref(false)
const popoverStyle = ref({})

let hideTimer = null

const arrowStyle = computed(() => {
  return {
    [`border-${props.placement}-color`]: props.arrowColor
  }
})

const handleMouseEnter = () => {
  if (props.disabled || props.trigger !== 'hover') return
  clearTimeout(hideTimer)
  showPopover()
}

const handleMouseLeave = () => {
  if (props.disabled || props.trigger !== 'hover') return
  hideTimer = setTimeout(() => {
    hidePopover()
  }, 200)
}

const handlePopoverMouseEnter = () => {
  if (props.trigger !== 'hover') return
  clearTimeout(hideTimer)
}

const handlePopoverMouseLeave = () => {
  if (props.trigger !== 'hover') return
  hideTimer = setTimeout(() => {
    hidePopover()
  }, 200)
}

const handleClick = () => {
  if (props.disabled || props.trigger !== 'click') return
  if (visible.value) {
    hidePopover()
  } else {
    showPopover()
  }
}

const showPopover = async () => {
  visible.value = true
  emit('show')
  await nextTick()
  updatePosition()
}

const hidePopover = () => {
  visible.value = false
  emit('hide')
}

const updatePosition = async () => {
  if (!triggerRef.value || !popoverRef.value) return
  const triggerRect = triggerRef.value.getBoundingClientRect()
  const popoverRect = popoverRef.value.getBoundingClientRect()

  const triggerWidth = triggerRef.value.offsetWidth
  const triggerHeight = triggerRef.value.offsetHeight

  const popoverWidth = popoverRef.value.offsetWidth
  const popoverHeight = popoverRef.value.offsetHeight

  const { offset } = props

  let top = 0
  let left = 0

  switch (props.placement) {
    case 'top':
      top = triggerRect.top - popoverHeight - offset
      left = triggerRect.left + (triggerWidth - popoverWidth) / 2
      break
    case 'bottom':
      top = triggerRect.bottom + offset
      left = triggerRect.left + (triggerWidth - popoverWidth) / 2
      break
    case 'left':
      top = triggerRect.top + (triggerHeight - popoverHeight) / 2
      left = triggerRect.left - popoverWidth - offset
      break
    case 'right':
      top = triggerRect.top + (triggerHeight - popoverHeight) / 2
      left = triggerRect.right + offset
      break
  }

  // 边界检测和调整
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  if (left < 0) left = 8
  if (left + popoverRect.width > viewportWidth) {
    left = viewportWidth - popoverRect.width - 8
  }
  if (top < 0) top = 8
  if (top + popoverRect.height > viewportHeight) {
    top = viewportHeight - popoverRect.height - 8
  }

  popoverStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 10000
  }
}

// 点击外部关闭
const handleClickOutside = (event) => {
  if (props.trigger !== 'click' || !visible.value) return

  if (
    triggerRef.value &&
    popoverRef.value &&
    !triggerRef.value.contains(event.target) &&
    !popoverRef.value.contains(event.target)
  ) {
    hidePopover()
  }
}

// 滚动时关闭悬浮窗
const handleScroll = () => {
  if (visible.value) {
    hidePopover()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', updatePosition)
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', handleScroll)
  clearTimeout(hideTimer)
})
</script>

<style lang='less' scoped>
.popover-trigger {
  display: inline-block;
}

.popover {
  position: fixed;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);

  &__content {
    background: #fff;
    border-radius: 8px;
  }

  &__arrow {
    position: absolute;
    width: 0;
    height: 0;
  }

  // 箭头位置
  &--top {
    .popover__arrow {
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #fff;
      border-bottom: none;
    }
  }

  &--bottom {
    .popover__arrow {
      top: -6px;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-bottom-color: #fff;
      border-top: none;
    }
  }

  &--left {
    .popover__arrow {
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      border: 6px solid transparent;
      border-left-color: #fff;
      border-right: none;
    }
  }

  &--right {
    .popover__arrow {
      left: -6px;
      top: 50%;
      transform: translateY(-50%);
      border: 6px solid transparent;
      border-right-color: #fff;
      border-left: none;
    }
  }
}

// 过渡动画
.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.popover-fade-enter-from,
.popover-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
